import { useEffect, useCallback } from "react";
import { useQueries } from "@tanstack/react-query";
import {
  CartItem,
  CartItemDesign,
  CartItemLogo,
} from "../presentation/screens/order-create/models/CartItem";
import { BaseLogo } from "../core/models/entities/BaseLogo";
import { Design } from "../core/models/entities/Design";
import { useProfileContext } from "../contexts/ProfileContext";
import { useApiClient } from "../data/clients/ApiClient";
import { mapToLogo } from "../data/models/mappers/mapLogo";
import { mapToDesign } from "../data/models/mappers/mapDesign";

interface UseCartSyncProps {
  items: CartItem[];
  updateItem: (item: CartItem) => Promise<CartItem>;
}

interface UseCartSyncReturn {
  isRefreshing: boolean;
  hasStaleItems: boolean;
  refreshCart: () => void;
}

export function useCartSync({
  items,
  updateItem,
}: UseCartSyncProps): UseCartSyncReturn {
  const apiClient = useApiClient();
  const { profile } = useProfileContext();

  // Extract logo items from cart
  const logoItems = items.filter(
    (item): item is CartItemLogo => item.type === "Logo"
  );

  // Extract design items from cart
  const designItems = items.filter(
    (item): item is CartItemDesign => item.type === "Design"
  );

  // Create queries for all logos in the cart
  const logoQueries = useQueries({
    queries: logoItems.map((logoItem) => ({
      queryKey: ["logos", logoItem.logoId],
      queryFn: () =>
        apiClient
          .getLogo(
            profile?.userOrganizationInformations.organizationId || 0,
            logoItem.logoId
          )
          .then(mapToLogo),
      enabled:
        !!profile?.userOrganizationInformations.organizationId &&
        logoItem.logoId > 0,
      staleTime: 1 * 30 * 1000,
    })),
  });

  // Create queries for all designs in the cart
  const designQueries = useQueries({
    queries: designItems.map((designItem) => ({
      queryKey: ["designs", designItem.design.id],
      queryFn: () =>
        apiClient
          .getDesign(
            designItem.design.customer?.id ||
              profile?.userOrganizationInformations.organizationId ||
              0,
            designItem.design.id
          )
          .then(mapToDesign),
      enabled:
        (!!designItem.design.customer?.id ||
          !!profile?.userOrganizationInformations.organizationId) &&
        designItem.design.id > 0,
      staleTime: 1 * 30 * 1000,
    })),
  });

  // Check if any queries are loading
  const isRefreshing =
    logoQueries.some((query) => query.isLoading || query.isFetching) ||
    designQueries.some((query) => query.isLoading || query.isFetching);

  // Function to check if a logo has stale data
  const isLogoStale = useCallback(
    (cartLogo: CartItemLogo, freshLogo: BaseLogo): boolean => {
      // Compare prices directly using product.prices
      const cartPrices = cartLogo.baseLogo.product?.prices || [];
      const freshPrices = freshLogo.product?.prices || [];

      if (cartPrices.length !== freshPrices.length) {
        return true;
      }

      // Check if any price has changed
      const hasChangedPrice = cartPrices.some((cartPrice, index) => {
        const freshPrice = freshPrices[index];
        const priceChanged =
          !freshPrice ||
          cartPrice.adjustedPrice !== freshPrice.adjustedPrice ||
          cartPrice.adjustedUnitPrice !== freshPrice.adjustedUnitPrice ||
          cartPrice.adjustedCustomerPrice !==
            freshPrice.adjustedCustomerPrice ||
          cartPrice.minQuantity !== freshPrice.minQuantity;

        return priceChanged;
      });

      return hasChangedPrice;
    },
    []
  );

  // Function to check if a design has stale data
  const isDesignStale = useCallback(
    (cartDesign: CartItemDesign, freshDesign: Design): boolean => {
      // Check if design logos have changed in storage or prices
      // This is simplified; ideally we check everything, but focusing on inStorage
      const cartLogos = cartDesign.design.designLogos || [];
      const freshLogos = freshDesign.designLogos || [];

      if (cartLogos.length !== freshLogos.length) return true;

      // Check if any logo has changed storage
      return cartLogos.some((cartLogo) => {
        const freshLogo = freshLogos.find((f) => f.logoId === cartLogo.logoId);
        if (!freshLogo) return true;

        const cartStorage = cartLogo.logo.inStorage ?? 0;
        const freshStorage = freshLogo.logo.inStorage ?? 0;

        if (cartStorage !== freshStorage) return true;

        // Also check prices of logos if needed, similar to isLogoStale
        // But inStorage is the main concern here
        return false;
      });
    },
    []
  );

  // Function to update stale cart items
  const syncStaleItems = useCallback(async () => {
    const updates: Promise<CartItem>[] = [];

    logoQueries.forEach((query, index) => {
      const cartItem = logoItems[index];
      const freshLogo = query.data;

      if (query.isSuccess && freshLogo && isLogoStale(cartItem, freshLogo)) {
        // Create updated cart item with fresh logo data
        const updatedCartItem: CartItemLogo = {
          ...cartItem,
          baseLogo: freshLogo,
        };

        updates.push(updateItem(updatedCartItem));
      }
    });

    designQueries.forEach((query, index) => {
      const cartItem = designItems[index];
      const freshDesign = query.data;

      if (
        query.isSuccess &&
        freshDesign &&
        isDesignStale(cartItem, freshDesign)
      ) {
        // Create updated cart item with fresh design data
        const updatedCartItem: CartItemDesign = {
          ...cartItem,
          design: freshDesign,
          // We need to verify if instances need updating?
          // Instances contain copies of logos. They should ideally be updated too.
          // But CartItemDesign stores instances which have `logos` array.
          // Each logo in instance has `baseLogo`.
          // We should update the instances' logos' baseLogos as well.
          instances: cartItem.instances.map((inst) => ({
            ...inst,
            logos: inst.logos.map((l) => {
              // Find fresh logo for this instance logo
              const freshLogoWrapper = freshDesign.designLogos?.find(
                (dl) => dl.logoId === l.logoId
              );
              if (freshLogoWrapper) {
                return { ...l, baseLogo: freshLogoWrapper.logo };
              }
              return l;
            }),
          })),
        };

        updates.push(updateItem(updatedCartItem));
      }
    });

    if (updates.length > 0) {
      try {
        await Promise.all(updates);
      } catch (error) {
        console.error("Failed to update stale cart items:", error);
      }
    }
  }, [
    logoQueries,
    designQueries,
    logoItems,
    designItems,
    isLogoStale,
    isDesignStale,
    updateItem,
  ]);

  // Check if there are any stale items
  const hasStaleItems =
    logoQueries.some((query, index) => {
      const cartItem = logoItems[index];
      const freshLogo = query.data;
      return query.isSuccess && freshLogo && isLogoStale(cartItem, freshLogo);
    }) ||
    designQueries.some((query, index) => {
      const cartItem = designItems[index];
      const freshDesign = query.data;
      return (
        query.isSuccess && freshDesign && isDesignStale(cartItem, freshDesign)
      );
    });

  // Auto-sync when fresh data is available
  useEffect(() => {
    if (
      !isRefreshing &&
      (logoQueries.length > 0 || designQueries.length > 0) &&
      logoQueries.every((q) => q.isSuccess || q.isError) &&
      designQueries.every((q) => q.isSuccess || q.isError)
    ) {
      syncStaleItems();
    }
  }, [isRefreshing, logoQueries, designQueries, syncStaleItems]);

  // Manual refresh function
  const refreshCart = useCallback(() => {
    logoQueries.forEach((query) => {
      if (query.refetch) {
        query.refetch();
      }
    });
    designQueries.forEach((query) => {
      if (query.refetch) {
        query.refetch();
      }
    });
  }, [logoQueries, designQueries]);

  return {
    isRefreshing,
    hasStaleItems,
    refreshCart,
  };
}
